import { useLayoutEffect, useRef, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { safariBleed } from '../../../hooks/useShellChrome';
import s from './FootBand.module.css';

/**
 * FootBand — the colour Safari's bottom toolbar takes (ui-design-system § Foot
 * band). Safari 26 paints the strip under its floating toolbar from the ROOT
 * background unless a solid full-width element sits on the bottom edge; the
 * root is a screen's TOP colour (useShellChrome), so a screen whose foot is a
 * different colour needs this band. Device-proven on Detail (strip probe v9,
 * 2026-10-14) and on the guest exchange (Mark's v7972 recording, 2026-10-19).
 *
 * ONE band on the edge at a time (signup E3a, Mark 2026-10-19: "Can't we always
 * make it white when there's a bottom sheet?" — the bar is the surface on the
 * bottom edge). Every FootBand is a CLAIM; only the top claim renders, the rest
 * step aside: the highest `layer` (a sheet over the page), then the newest —
 * so a page band that mounts late never covers a sheet already up. SheetOverlay claims while it is open, so a sheet's colour
 * replaces the card's or the page's band — never beside it (with two fixed
 * bands, which one Safari reads is unproven) — and when it lets go the one
 * under it comes back as a fresh element.
 *
 * Safari samples the band when it APPEARS, never when it is recoloured (Mark's
 * v7973 / v7974 recordings): a colour change re-mounts the element (keyed by
 * its colour), and a band that steps aside and back is mounted anew.
 *
 * Rendered into document.body: fixed against the screen whatever its host
 * (a blurred or transformed ancestor would otherwise contain it).
 *
 * Safari tab only — the home-screen app has no toolbar (safariBleed() is 0).
 *
 * @param {Object} p
 * @param {string} [p.colour='var(--card-bg)'] - what the toolbar should read as
 * @param {number} [p.zIndex=10] - above whatever the screen paints at its foot
 * @param {number} [p.layer=0] - 0 the page (a card's foot, Detail's band); 1 a
 *   sheet over it (SheetOverlay)
 */
const FootBand = ({ colour = 'var(--card-bg)', zIndex = 10, layer = 0 }) => {
  const on = safariBleed() > 0;
  const id = useRef(null);
  if (id.current == null) id.current = nextId();
  useLayoutEffect(() => {
    if (!on) return undefined;
    return claim(id.current, layer);
  }, [on, layer]);
  const top = useSyncExternalStore(subscribe, topClaim);
  if (!on || top !== id.current) return null;
  return createPortal(
    <div
      key={colour}
      className={s.band}
      style={{ background: colour, zIndex }}
      aria-hidden="true"
      data-foot-band={colour}
    />,
    document.body
  );
};

// The claims ({ id, layer }), oldest first.
let claims = [];
let seq = 0;
const listeners = new Set();
const nextId = () => {
  seq += 1;
  return seq;
};
const emit = () => listeners.forEach((l) => l());
const subscribe = (l) => {
  listeners.add(l);
  return () => listeners.delete(l);
};
// The highest layer; within it the newest
const topClaim = () => {
  let top = null;
  for (const c of claims) if (!top || c.layer >= top.layer) top = c;
  return top ? top.id : null;
};
const claim = (cid, layer) => {
  claims = [...claims, { id: cid, layer }];
  emit();
  return () => {
    claims = claims.filter((c) => c.id !== cid);
    emit();
  };
};

export default FootBand;
